import NestedMethods from '@/configuration/sources/smartform/NestedMethods.yml'
import { MethodDescriptor } from '@/common/model/PaymentMethod'

/**
 * @typedef {Object} NestedMethodCacheEntry
 * @property {string | null} currentComponent
 * @property {{ expiry: number | null, warnExpiry: number | null }} expiration
 * @property {* | null} testData
 * @property {{ topLines: string[] | null, bottomLines: string[] | null }} labels
 */

export const getNestedMethodState = () => {
  return {
    nestedMethod: {
      extraFields: {},
      mockCountdownValue: null,
      testData: null,
      /**
       * @type {Record<string, NestedMethodCacheEntry>}
       */
      methods: {} // Initialization not required to allow reactivity, vue3 handles new properties
    }
  }
}

export const nestedMethodActions = {
  nestedMethodSetup({ commit, getters }, method) {
    if (!method) return

    const { metadata } = MethodDescriptor.create(method)
    if (getters.cardFormInsideButton(method)) {
      commit('UPDATE', {
        activeForm: metadata
      })
    }
  },
  setNestedMethodExtraFields({ commit }, fields) {
    commit('UPDATE', {
      smartForm: {
        nestedMethod: {
          extraFields: fields
        }
      }
    })
  },
  setNestedMethodTestData({ commit }, testData) {
    // Reset first
    commit('UPDATE', {
      smartForm: {
        nestedMethod: {
          testData: null
        }
      }
    })

    const updateObj = {
      smartForm: {
        nestedMethod: {
          testData
        }
      }
    }

    // Add test data to cache, required for methods that are kept alive after close
    if (testData)
      updateObj.smartForm.nestedMethod.methods = {
        [testData.nestedMethod]: {
          testData
        }
      }

    commit('UPDATE', updateObj)
  },
  setNestedMethodLabels({ commit, state }, { method, labels }) {
    commit('UPDATE', {
      smartForm: {
        nestedMethod: {
          methods: {
            [method]: {
              labels
            }
          }
        }
      }
    })
  },
  loadCachedTestData({ commit, state }, method) {
    const testData = state.smartForm.nestedMethod.methods[method]?.testData
    commit('UPDATE', {
      smartForm: {
        nestedMethod: {
          testData
        }
      }
    })
  },
  setCurrentNestedComponent({ commit }, { currentComponent, method }) {
    commit('UPDATE', {
      smartForm: {
        nestedMethod: {
          methods: {
            [method]: {
              currentComponent
            }
          }
        }
      }
    })
  },
  /**
   * Centralize expiry/countdown by method.
   *
   * @since KJS-6040
   */
  setMethodExpiry({ commit }, { method, expiry, warnExpiry = null }) {
    commit('UPDATE', {
      smartForm: {
        nestedMethod: {
          methods: {
            [method]: {
              expiration: {
                expiry,
                warnExpiry
              }
            }
          }
        }
      }
    })
  }
}

export const nestedMethodGetters = {
  /**
   * List of card brands to be used as a method (e.g: ILLICADO, OG_*) regardless
   * of whitelist.
   *
   * @since KJS-5546
   */
  cardBrandAsMethodRawList(state, getters) {
    const { dna } = state
    const { isSmartForm } = getters

    if (!isSmartForm || dna?.cards == null || typeof dna.cards !== 'object') {
      return []
    }

    return Object.keys(dna.cards).filter(brand => {
      if (NestedMethods.forceNested.includes(brand)) return true
      return dna.cards[brand]?.separateButton === true && brand !== 'DEFAULT'
    })
  },
  /**
   * Like cardBrandAsMethodRawList but non-whitelisted card/method are
   * filtered out. Also filters out excluded payment methods.
   * If allowedPaymentMethods doesn't contain 'CARDS', only explicitly allowed brands are included.
   *
   * @see KJS-5546
   */
  cardBrandAsMethodList(state, getters) {
    const { cardBrandAsMethodRawList, isMethodWhitelisted } = getters
    const { excludedPaymentMethods = [], allowedPaymentMethods } =
      state.smartForm

    const upperExcludedMethods = excludedPaymentMethods.map(m =>
      m.toUpperCase()
    )

    return cardBrandAsMethodRawList.filter(brand => {
      // Check if whitelisted
      if (!isMethodWhitelisted(brand)) return false

      // Check if excluded
      if (upperExcludedMethods.includes(brand)) return false

      // Check if allowed (only filter if allowedPaymentMethods is not 'all')
      if (!allowedPaymentMethods.includes('all')) {
        // If CARDS is not in allowedPaymentMethods, only keep brands that are explicitly allowed
        if (!allowedPaymentMethods.includes('CARDS')) {
          if (!allowedPaymentMethods.includes(brand.toUpperCase())) {
            return false
          }
        }
      }

      return true
    })
  },
  qrMethodList({ dna }) {
    if (!dna) return []

    const { smartForm } = dna
    const list = []
    for (const method in smartForm) {
      const methodConf = smartForm[method]
      if (methodConf.paymentFlowAvailable && !methodConf.fields)
        list.push(method)
    }

    return list
  },
  nestedFormMethodList({ dna }) {
    if (!dna) return []

    const { smartForm } = dna
    const list = []
    for (const method in smartForm) {
      const { fields } = smartForm[method]
      if (fields && (fields.cellPhoneNumber || fields.email)) list.push(method)
    }

    return list
  },
  activeNestedMethodAndMultipleGridColumns: (
    {
      smartForm: {
        otherPaymentMethodsLayout: {
          grid: { effectiveColumns: opmEffectiveColumns }
        },
        preferredPaymentMethodsLayout: {
          grid: { effectiveColumns: ppmEffectiveColumns }
        }
      }
    },
    { activeNestedMethod, isGridMode, isActiveNestedPreferredPM }
  ) => {
    const effectiveColumns = isActiveNestedPreferredPM
      ? ppmEffectiveColumns
      : opmEffectiveColumns

    return !!activeNestedMethod && isGridMode && effectiveColumns > 1
  },
  isActiveNestedAndCompactOrGridColumns: (
    _state,
    {
      activeNestedMethodAndMultipleGridColumns,
      activeNestedMethod,
      isSmartFormCompactApplied
    }
  ) => {
    return (
      activeNestedMethodAndMultipleGridColumns ||
      (activeNestedMethod && isSmartFormCompactApplied)
    )
  },
  // edge case in compact mode,payment methods: cards,illicado. Cards changes to expanded when selecting illicado since KJS-5327
  compactNestedMethodEdgeCase: (
    { smartForm },
    { activeNestedMethod, isSmartFormCompactApplied }
  ) =>
    activeNestedMethod &&
    isSmartFormCompactApplied &&
    smartForm.displayOptions.cardsIntegrated,
  getNestedBaseComponent(_state, getters) {
    const { getNestedMethodType } = getters
    return method => {
      const type = getNestedMethodType(method)

      return NestedMethods.components[type] ?? null
    }
  },
  // TODO refactor this, doesn't make sense anymore
  getNestedMethodType(_state, getters) {
    const {
      isNestedMethod,
      cardFormInsideButton,
      isQRMethod,
      isNestedFormMethod
    } = getters
    const types = [
      { getter: cardFormInsideButton, type: 'CARDFORM' },
      { getter: isQRMethod, type: 'QR' },
      { getter: isNestedFormMethod, type: 'FORM' }
    ]
    return method => {
      if (!method || !isNestedMethod(method)) return null
      const result = types.find(({ getter }) => getter(method))

      if (!result) return 'LOADING'
      return result.type
    }
  },
  keepAliveNestedMethod({ smartForm }, { getNestedMethodType }) {
    const {
      nestedMethod: { methods }
    } = smartForm
    return method => {
      // TODO - By default keepAlive everything
      // when we get it from the backend, use a parameter to determine which methods to NOT cache
      return true
    }
  },
  isNestedMethod(_state, getters) {
    const { cardFormInsideButton, isPaymentFlowMethod, isNestedFormMethod } =
      getters
    return method =>
      cardFormInsideButton(method) ||
      isPaymentFlowMethod(method) ||
      isNestedFormMethod(method)
  },
  isQRMethod(_state, { qrMethodList }) {
    return method => {
      if (!method) return
      method = MethodDescriptor.create(method).toString()

      return qrMethodList.includes(method)
    }
  },
  isNestedFormMethod(_state, { nestedFormMethodList }) {
    return method => {
      if (!method) return
      method = MethodDescriptor.create(method).toString()

      return nestedFormMethodList.includes(method)
    }
  },
  getNestedMethodFields({ dna }) {
    return method => {
      if (!dna) return []
      const dnaFields = dna.smartForm[method].fields
      if (!dnaFields) return []

      return dnaFields
    }
  },
  cardFormInsideButton(_state, getters) {
    const {
      isFormPopin,
      isSmartFormPopin,
      isCardBrandAsMethod,
      hasSmartButton
    } = getters

    return method => {
      if (!method) return
      method = MethodDescriptor.create(method).toString()
      return (
        ((!isFormPopin && !isSmartFormPopin) || hasSmartButton(method)) &&
        isCardBrandAsMethod(method)
      )
    }
  },
  hasCardFormInsideButton(state, getters) {
    const { isSmartForm, effectivePaymentMethodList, cardFormInsideButton } =
      getters

    const {
      smartForm: { smartButtons }
    } = state
    return (
      isSmartForm &&
      [...smartButtons, ...effectivePaymentMethodList].some(method =>
        cardFormInsideButton(method)
      )
    )
  },
  /**
   * Returns the MethodDescriptor for the active nested method (i.e. ILLICADO)
   * or null if none is active at the moment
   *
   * @returns {MethodDescriptor|null}
   */
  activeNestedMethod(state, getters) {
    const { activeMethod, activeMethodMetadata } = state.smartForm
    const { isNestedMethod } = getters

    if (!activeMethod) return null

    const method = MethodDescriptor.create([activeMethod, activeMethodMetadata])
    return isNestedMethod(method) ? method : null
  },
  // Methods nested inside the form element (without popup)
  // At the moment -> CARDS (if card form expanded) and ILLICADO
  isAnyNestedMethodActive(
    _state,
    { isExpandedCardFormActive, activeNestedMethod }
  ) {
    return isExpandedCardFormActive || !!activeNestedMethod
  },
  getNestedMethodExtraFields(state, { isNestedMethod }) {
    const {
      smartForm: {
        nestedMethod: { extraFields }
      }
    } = state
    return method => (isNestedMethod(method) ? extraFields : {})
  },
  effectiveNestedMethodTestData(
    state,
    { activeNestedMethod, cardFormInsideButton }
  ) {
    const {
      smartForm: {
        nestedMethod: { testData }
      }
    } = state
    if (cardFormInsideButton(activeNestedMethod)) {
      return { nestedMethod: activeNestedMethod.metadata, isCardBrand: true }
    }

    return !!activeNestedMethod && testData
  },
  isPaymentFlowMethod({ dna }) {
    return method => dna?.smartForm?.[method]?.paymentFlowAvailable
  },
  isNestedComponentActive(state, { activeNestedMethod, getShortMethodKey }) {
    const {
      smartForm: {
        nestedMethod: { methods }
      }
    } = state
    return componentType => {
      //  componentType: types on NestedMethods.yml
      if (!activeNestedMethod) return false
      return (
        methods[getShortMethodKey(activeNestedMethod)]?.currentComponent ===
        componentType
      )
    }
  },
  getMethodFields(state) {
    const { dna } = state
    return method => dna.smartForm[method].fields
  },
  getMethodSupportedFields(_state, { getMethodFields }) {
    const supportedFields = ['cellPhoneNumber', 'email']
    return method => {
      const fieldConf = getMethodFields(method)
      if (!fieldConf) return {}

      return Object.keys(fieldConf)
        .filter(key => supportedFields.includes(key))
        .reduce((acc, key) => {
          acc[key] = fieldConf[key]
          return acc
        }, {})
    }
  },
  hasMethodSupportedFields(_state, { getMethodSupportedFields }) {
    return method => Object.keys(getMethodSupportedFields(method)).length > 0
  },
  hasActiveComponent(state, { activeNestedMethod, getShortMethodKey }) {
    const {
      smartForm: {
        nestedMethod: { methods }
      }
    } = state
    if (!activeNestedMethod) return null

    return (
      methods[getShortMethodKey(activeNestedMethod)]?.currentComponent ?? null
    )
  },
  getMethodExpiryConf(state) {
    return method => {
      if (
        !method ||
        !state.smartForm.nestedMethod.methods?.[method]?.expiration
      )
        return null

      const { expiry, warnExpiry } =
        state.smartForm.nestedMethod.methods[method].expiration

      if (!expiry) return null

      return { expiry, warnExpiry }
    }
  },
  getMethodLabels(state) {
    return method => {
      if (!method) return null
      const labels = state.smartForm.nestedMethod.methods?.[method]?.labels
      if (!labels || (!labels.topLines && !labels.bottomLines)) return null

      return labels
    }
  },
  isCountdownVisible(
    _state,
    { activeNestedMethod, isActiveMethod, getMethodExpiryConf }
  ) {
    return method => {
      return (
        getMethodExpiryConf(method) &&
        !!activeNestedMethod &&
        isActiveMethod(method)
      )
    }
  }
}
